import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, withRouter } from 'react-router-dom';
import { useQuery } from '@helpers/utils/hooksUtils';
import { Box, ResponsiveContext, Text } from 'grommet';
import { UserContext } from '../../services/Session';
import {
  usePinnedClips,
  usePlayerGameSummaries,
  usePlayerPrimaryInfo,
  useVideoDimensions,
} from './playerHooks';

import useEntitlements from '@hooks/monetization/useEntitlements';

import PlayerCardV2 from './PlayerCardV2';
import { shd } from '@shd/jslib/models';
import PlayerSportToggle from './PlayerSportToggle';
import { observer } from 'mobx-react-lite';
import {
  BASEBALL,
  COMBINED_BBSB_KEY,
  SOFTBALL,
  VOLLEYBALL,
} from '../../constants/strings';
import PlayerClipsSection from './PlayerClipsSection';
import { ClipFeedClip, PlayerPageProps } from './player.types';
import axios from '../../helpers/axios';
import * as Sentry from '@sentry/browser';
import GameSummaryCarousel from './GameSummaryCarousel';
import ClaimWall from './ClaimWall';
import ClaimPlayerLayer from './ClaimPlayerLayer';
import { useToast } from '../../contexts/ToastContext';
import { HiSparkles } from 'react-icons/hi';
import { UserType, analytics } from '../../services/analytics';
import withLayout from '../../helpers/withLayout';
import PlayerClaimPrompt from './PlayerClaimPrompt';
import PlayerHitScope from './PlayerHitScope';
import { usePermitClaimPlayer } from '../../hooks/usePlayerClaims';
import {
  ViewerEntitlementStatus,
  PaywallPageContext,
} from '../../components/Paywall/paywall.types';

import BackButton from '../../components/BackButton/BackButton';
import SportsRecruitsBanner from '../SportsRecruits/SportsRecruitsBanner';
import SportsRecruitsInfoModal from './SportsRecruitsInfoModal';
import { usePlayerGameStats } from '@hooks/useUserStats';
import { usePaywallContext } from '@contexts/PaywallContext';
import NotReadyToGoProYet from './NotReadyToGoProYet';
import './PlayerPage.css';

interface PlayerPageFirstData {
  nameFirst: string;
  clipCount: number;
  opponent: string;
}

const PlayerPage: React.FC<PlayerPageProps> = observer(
  ({ claimed, playerId, history }) => {
    const query = useQuery();
    const location = useLocation();
    const openPlayerSettings = Boolean(query?.get('open_settings') || 0);
    const [localPlayerId, setLocalPlayerId] = useState<string>(playerId);
    const [isClaimed, setIsClaimed] = useState<boolean>(claimed);
    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const screen = useContext(ResponsiveContext);
    const userStore = useContext(UserContext);
    const { showToast } = useToast();
    const videoDimensions = useVideoDimensions();

    // @note This should be moved to a utility function once the utils directory
    //       is organized and merged.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isTestGroup = (key: string): boolean => {
      const keyNormalized = key.toLowerCase();
      const searchParams = new URLSearchParams(location.search.toLowerCase());
      return searchParams.get(keyNormalized) !== null;
    };

    // @note This should be moved to a utility function once the utils directory
    //       is organized and merged.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getSearchParamsAsObject = (): Record<string, string> => {
      const searchParams = new URLSearchParams(location.search);
      const params: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
      return params;
    };

    const isPlayerPageFirstGroup = isTestGroup('playerPageFirst');
    const playerPageFirstParams = isPlayerPageFirstGroup
      ? getSearchParamsAsObject()
      : null;

    const [playerPageFirstData, setPlayerPageFirstData] =
      useState<PlayerPageFirstData>();

    const { getPlayerGameStats } = usePlayerGameStats();

    useEffect(() => {
      if (
        !playerPageFirstData &&
        isPlayerPageFirstGroup &&
        playerPageFirstParams &&
        !playerId.includes('TpSh01')
      ) {
        getPlayerGameStats(
          playerId,
          playerPageFirstParams?.highlight_game_id
        ).then((data) => {
          if (data) {
            setPlayerPageFirstData({
              nameFirst: data.nameFirst,
              clipCount: data.clipCount,
              opponent: data.opponent,
            } as PlayerPageFirstData);
          }
        });
      }
    }, [
      playerPageFirstData,
      getPlayerGameStats,
      playerId,
      playerPageFirstParams,
      isPlayerPageFirstGroup,
    ]);

    const {
      playerInfo: playerPrimaryInfo,
      loading: playerInfoLoading,
      error: playerInfoError,
    } = usePlayerPrimaryInfo(localPlayerId);

    const [selectedSport, setSelectedSport] = useState('');

    useEffect(() => {
      //if this team player is claimed, redirect to their handle
      if (playerPrimaryInfo?.claimedPlayerId) {
        history.push(`/${playerPrimaryInfo.claimedPlayerHandle}`);
        return;
      }

      const checkForPrimarySport = () => {
        let primaryTeamId: string = '';
        if (playerPrimaryInfo?.player.claimedPlayerId === '') {
          primaryTeamId = playerPrimaryInfo?.player.DNOR_teamId || '';
        } else {
          // If the player is claimed, we
          primaryTeamId =
            playerPrimaryInfo?.player?.DNOR_teamIdPriority?.[0] || '';
        }
        const playerPrimarySport = playerPrimaryInfo?.teams?.find(
          (team) => team._id === primaryTeamId
        ) as shd.Team | undefined;

        return playerPrimarySport?.attrib_sportType;
      };

      const qp = query.get('sport');
      const playerPrimarySport = playerPrimaryInfo && checkForPrimarySport();
      if (!qp) {
        if (playerPrimarySport) {
          if (playerPrimarySport === 'Volleyball') {
            setSelectedSport('vb');
          } else if (
            playerPrimarySport === 'Baseball' ||
            playerPrimarySport === 'Softball'
          ) {
            setSelectedSport(COMBINED_BBSB_KEY);
          } else {
            setSelectedSport(
              playerPrimarySport?.toLowerCase().replace(' ', '_') || ''
            );
          }
        }
      } else {
        setSelectedSport(qp);
      }
    }, [playerPrimaryInfo, query, history]);

    const userTeamIds = userStore.userTeams
      ? userStore.userTeams.map((team) => team._id)
      : [];

    const userPlayerIds = userStore.userPlayers
      ? userStore.userPlayers.map((p) => p._id)
      : [];

    const loggedInUserId = userStore.authUser?.claims?.shd_user_id;
    const userCanClaim = usePermitClaimPlayer(
      playerId,
      playerPrimaryInfo?.teams?.[0]?._id,
      loggedInUserId
    );

    const isAdmin = userPlayerIds.includes(playerId);

    const {
      entitlementStatus: viewerEntitlementStatus,
      loading: isEntitledToViewLoading,
      error: isEntitledToViewError,
    } = useEntitlements({ playerId, sport: selectedSport });

    const { renderPaywallLock, handlePaywallOpen } = usePaywallContext({
      entitlementStatus: viewerEntitlementStatus,
      trackingContext: PaywallPageContext.PLAYER_PAGE,
    });

    const { pinnedClips: initialPinnedClips, loading: pinnedClipsLoading } =
      usePinnedClips(
        viewerEntitlementStatus === ViewerEntitlementStatus.ENTITLED
          ? localPlayerId
          : null,
        isClaimed
      );
    const [localPinnedClips, setLocalPinnedClips] = useState<ClipFeedClip[]>(
      []
    );

    const isFollower =
      playerPrimaryInfo?.teams &&
      playerPrimaryInfo.teams.length &&
      userStore.teamFollowerIds
        .concat(userTeamIds)
        .filter((teamId) =>
          playerPrimaryInfo.teams
            .map((playerTeam) => playerTeam._id)
            .includes(teamId)
        ).length;

    const {
      gameSummaries,
      error: gameSummariesError,
      loading: gameSummariesLoading,
      fetchMore,
      hasMore,
    } = usePlayerGameSummaries(playerId, selectedSport);

    const [localPlayer, setLocalPlayer] = useState<shd.Player | null>(null);
    const [teams, setTeams] = useState<shd.Team[] | null>(null);
    const getClaimedPlayer = (): shd.ClaimedPlayer | null =>
      claimed ? (localPlayer as shd.ClaimedPlayer) : null;
    const claimedPlayer = getClaimedPlayer();
    const openClaimModal = Boolean(query?.get('open_claim_modal') || 0);
    const fromClaimWall = Boolean(query?.get('from_claim_wall') || 0);
    const [showClaimWall, setShowClaimWall] = useState(false);
    const [showClaim, setShowClaim] = useState(openClaimModal || fromClaimWall);

    useEffect(() => {
      const determineUserType = () => {
        let userType;
        if (!userStore.authUser) {
          userType = UserType.LoggedOut;
        } else if (isAdmin) {
          userType = UserType.Admin;
        } else if (isFollower) {
          userType = UserType.CommunityMember;
        } else {
          userType = UserType.LoggedIn;
        }
        return userType;
      };

      if (localPlayer) {
        analytics.track('Player Page View', {
          playerId: localPlayer._id,
          sport: selectedSport,
          hasRecommendation: false,
          userType: determineUserType(),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localPlayer, selectedSport]);

    useEffect(() => {
      if (playerPrimaryInfo?.player && playerPrimaryInfo.teams) {
        setLocalPlayer(playerPrimaryInfo.player);
        setTeams(playerPrimaryInfo.teams);

        // set selected sport based on what teams the player is on
        const sports = playerPrimaryInfo.teams.map(
          (team) => team.attrib_sportType
        );
        const isBBSB =
          sports.includes(BASEBALL.display) ||
          sports.includes(SOFTBALL.display);
        if (playerPrimaryInfo.teams.length) {
          if (selectedSport === COMBINED_BBSB_KEY && !isBBSB) {
            const nextSport = playerPrimaryInfo.teams[0].attrib_sportType
              .toLowerCase()
              .replace(' ', '_');
            if (nextSport === VOLLEYBALL.key) {
              setSelectedSport('vb');
            } else {
              setSelectedSport(nextSport);
            }
          }
        }
      } else if (playerPrimaryInfo?.claimedPlayerId) {
        setLocalPlayerId(playerPrimaryInfo.claimedPlayerId);
        setIsClaimed(true);
        history.replace({
          pathname: playerPrimaryInfo.claimedPlayerHandle,
          search: location.search,
          hash: location.hash,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerPrimaryInfo, claimedPlayer]);

    useEffect(() => {
      if (!pinnedClipsLoading) {
        setLocalPinnedClips(initialPinnedClips);
      }
    }, [initialPinnedClips, pinnedClipsLoading]);

    const pinClip = (clip: ClipFeedClip) => {
      const body = {
        claimedPlayerId: playerId,
        refPreview: clip.refPreview,
        id: clip.id,
      };
      axios
        .post(`/api/clip/${clip.id}/pin`, body)
        .then((response) => {
          if (response.data.sentToSportsRecruits) {
            showToast({
              icon: <HiSparkles />,
              message: 'Added to your SportsRecruits profile',
            });
          }
        })
        .catch((respErr) => {
          Sentry.captureException(respErr);
        });
      const newClip = { ...clip };
      const regex = /vs (.*)/;
      const matchArr = newClip.textDescription?.match(regex) || [];
      let opponentName = '';
      if (matchArr.length >= 1) {
        [, opponentName] = matchArr;
      }
      newClip.opponentName = opponentName;
      const newPinnedClips = localPinnedClips.concat(newClip);
      const sortedNewPinnedClips = newPinnedClips.sort(
        (c1, c2) => c2.clipStartTs - c1.clipStartTs
      );
      setLocalPinnedClips(sortedNewPinnedClips);

      showToast({
        icon: <HiSparkles />,
        message: 'Added to your highlights',
      });
    };

    const unpinClip = (clip: ClipFeedClip) => {
      const body = {
        playerId,
        refPreview: clip.refPreview,
        id: clip.id,
      };
      axios
        .delete(`/api/clip/${clip.id}/pin`, { data: body })
        .catch((respErr) => {
          Sentry.captureException(respErr);
        });

      // TODO: pinnedClips may need to ids hashed
      const newPinnedClips = localPinnedClips.filter((c) => c.id !== clip.id);
      setLocalPinnedClips(newPinnedClips);

      showToast({
        icon: <HiSparkles />,
        message: 'Removed from your highlights',
      });
    };

    const handlePlayerPageFirstOpenClips = useCallback(
      (clipsContainer: HTMLElement): void => {
        const myClipsButton = clipsContainer.querySelector(
          '.button-my-clips'
        ) as HTMLElement;
        myClipsButton && myClipsButton.click();
      },
      []
    );

    useEffect(() => {
      if (
        isPlayerPageFirstGroup &&
        !playerInfoLoading &&
        !gameSummariesLoading &&
        !isEntitledToViewLoading &&
        viewerEntitlementStatus === ViewerEntitlementStatus.ENTITLED
      ) {
        const checkForGameClips = setTimeout(() => {
          const selectedGameClips = document.querySelector(
            `#${playerPageFirstParams?.highlight_game_id}-offense_batter`
          ) as HTMLElement;

          if (selectedGameClips && selectedGameClips.offsetTop > 200) {
            handlePlayerPageFirstOpenClips(selectedGameClips);
            clearTimeout(checkForGameClips);
          }
        }, 1000);

        return () => {
          clearTimeout(checkForGameClips);
        };
      }
    }, [
      isPlayerPageFirstGroup,
      playerPrimaryInfo,
      playerInfoLoading,
      gameSummariesLoading,
      isEntitledToViewLoading,
      playerPageFirstParams,
      viewerEntitlementStatus,
      handlePlayerPageFirstOpenClips,
    ]);

    return (
      <Box
        fill="vertical"
        margin="auto"
        width={screen === 'small' ? '100%' : `${videoDimensions.width}px`}
      >
        {userStore.authUser && (
          <BackButton
            style={{
              left: '1rem',
              marginTop: '1rem',
              position: 'relative',
            }}
          />
        )}

        {isClaimed && (
          <PlayerSportToggle
            teams={teams || []}
            selectedSport={selectedSport}
            onSportChange={(sport) => {
              const newParams = new URLSearchParams();
              newParams.set('sport', sport);

              history.push({
                search: newParams.toString(),
              });

              setSelectedSport(sport);
            }}
          />
        )}
        {!isClaimed && userCanClaim && (
          <PlayerClaimPrompt setShowClaim={setShowClaim} />
        )}
        <Box pad="medium" skeleton={playerInfoLoading}>
          <PlayerCardV2
            player={localPlayer}
            teams={teams}
            isLoading={playerInfoLoading}
            claimed={isClaimed}
            isAdmin={isAdmin}
            openPlayerSettings={openPlayerSettings}
            setShowSRInfoModal={setShowInfoModal}
            selectedSport={selectedSport}
          />
          {playerInfoError && <Text>Error loading player info</Text>}
        </Box>
        {showInfoModal && (
          <SportsRecruitsInfoModal
            player={localPlayer}
            onClose={() => setShowInfoModal(false)}
            showCTA={true}
          />
        )}

        {claimedPlayer && (
          <SportsRecruitsBanner
            player={claimedPlayer}
            isEntitledToView={
              !isEntitledToViewLoading &&
              viewerEntitlementStatus === ViewerEntitlementStatus.ENTITLED
            }
            teams={teams}
            isAdmin={isAdmin}
            claimed={isClaimed}
            openSRModal={false}
          />
        )}
        <Box
          className="player-content"
          id="player-content"
          height={{ min: '400px' }}
        >
          {!isEntitledToViewLoading &&
          viewerEntitlementStatus ===
            ViewerEntitlementStatus.ENTITLED_BUT_PRIVATE ? (
            renderPaywallLock({
              isClaimed: isClaimed,
              isLoggedIn: !!userStore.authUser,
              isPrivate: localPlayer?.publicPublishLevel !== 10,
              onPaywallOpen: handlePaywallOpen,
              context: PaywallPageContext.PLAYER_PAGE,
            })
          ) : (
            <>
              <Box
                className={`player-content__assets${viewerEntitlementStatus !== ViewerEntitlementStatus.ENTITLED ? ' player-content__assets--blur' : ''}`}
              >
                <Box skeleton={gameSummariesLoading}>
                  {!gameSummariesError && (
                    <GameSummaryCarousel
                      gameSummaries={gameSummaries}
                      fetchMore={fetchMore}
                      hasMore={hasMore}
                    />
                  )}
                </Box>

                {(isAdmin ||
                  isFollower ||
                  localPlayer?.publicPublishLevel === 10) &&
                  selectedSport === COMBINED_BBSB_KEY && (
                    <PlayerHitScope
                      playerId={playerId}
                      claimed={claimed}
                      history={history}
                      setShowClaimWall={setShowClaimWall}
                    />
                  )}
                {teams && teams.length > 0 && (
                  <PlayerClipsSection
                    teams={teams}
                    selectedSport={selectedSport}
                    playerId={playerId}
                    player={localPlayer}
                    claimed={isClaimed}
                    isAdmin={isAdmin}
                    pinClip={pinClip}
                    unpinClip={unpinClip}
                    pinnedClips={localPinnedClips}
                    showReelsTab={true}
                    onShowClaim={() => {
                      setShowClaim(true);
                    }}
                  />
                )}
              </Box>

              {!isEntitledToViewLoading &&
                viewerEntitlementStatus !==
                  ViewerEntitlementStatus.ENTITLED && (
                  <Box
                    className="player-content__paywall"
                    pad="medium"
                    skeleton={playerInfoLoading || isEntitledToViewLoading}
                  >
                    {!isEntitledToViewLoading &&
                      viewerEntitlementStatus ===
                        ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_IN &&
                      !claimed && (
                        <Box className="player-content__paywall-container">
                          {renderPaywallLock({
                            isClaimed,
                            isLoggedIn: !!userStore.authUser,
                            mode: 'teamPlayer',
                            isPrivate: localPlayer?.publicPublishLevel !== 10,
                            handlePaywallOpen: handlePaywallOpen,
                            type: 'lock',
                            context: PaywallPageContext.PLAYER_PAGE,
                          })}
                        </Box>
                      )}

                    {!isEntitledToViewLoading &&
                      viewerEntitlementStatus ===
                        ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_IN &&
                      claimed && (
                        <>
                          {isPlayerPageFirstGroup && playerPageFirstData ? (
                            <Box className="player-content__paywall-container">
                              {renderPaywallLock({
                                type: 'lock',
                                context: PaywallPageContext.PLAYER_PAGE,
                                isClaimed: isClaimed,
                                isLoggedIn: !!userStore.authUser,
                                isPrivate:
                                  localPlayer?.publicPublishLevel !== 10,
                                handlePaywallOpen: handlePaywallOpen,
                                customCopy: {
                                  headline: `Watch all ${playerPageFirstData.clipCount} of ${playerPageFirstData.nameFirst}'s highlight clips against ${playerPageFirstData.opponent}`,
                                },
                              })}

                              <NotReadyToGoProYet
                                isEntitledToViewLoading={
                                  isEntitledToViewLoading
                                }
                                highlightGameId={
                                  playerPageFirstParams?.highlight_game_id
                                }
                              />
                            </Box>
                          ) : (
                            <Box className="player-content__paywall-container">
                              {renderPaywallLock({
                                isClaimed,
                                isLoggedIn: !!userStore.authUser,
                                mode: 'teamPlayer',
                                isPrivate:
                                  localPlayer?.publicPublishLevel !== 10,
                                handlePaywallOpen: handlePaywallOpen,
                                type: 'lock',
                                context: PaywallPageContext.PLAYER_PAGE,
                                playerId: playerId,
                              })}
                            </Box>
                          )}
                        </>
                      )}

                    {!isEntitledToViewLoading &&
                      viewerEntitlementStatus ===
                        ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_OUT && (
                        <>
                          {claimed &&
                          isPlayerPageFirstGroup &&
                          playerPageFirstData ? (
                            <Box className="player-content__paywall-container">
                              {renderPaywallLock({
                                type: 'lock',
                                context: PaywallPageContext.PLAYER_PAGE,
                                isClaimed: isClaimed,
                                isLoggedIn: !!userStore.authUser,
                                isPrivate:
                                  localPlayer?.publicPublishLevel !== 10,
                                handlePaywallOpen: handlePaywallOpen,
                                customCopy: {
                                  headline: `Watch all ${playerPageFirstData.clipCount} of ${playerPageFirstData.nameFirst}'s highlight clips against ${playerPageFirstData.opponent}`,
                                },
                              })}

                              <NotReadyToGoProYet
                                isEntitledToViewLoading={
                                  isEntitledToViewLoading
                                }
                                highlightGameId={
                                  playerPageFirstParams?.highlight_game_id
                                }
                              />
                            </Box>
                          ) : (
                            <Box className="player-content__paywall-container">
                              {renderPaywallLock({
                                type: 'lock',
                                context: PaywallPageContext.PLAYER_PAGE,
                                isClaimed: isClaimed,
                                isLoggedIn: !!userStore.authUser,
                                isPrivate:
                                  localPlayer?.publicPublishLevel !== 10,
                                handlePaywallOpen: handlePaywallOpen,
                              })}
                            </Box>
                          )}
                        </>
                      )}

                    {isEntitledToViewError}
                    {isEntitledToViewError && (
                      <Box>Error loading subscription status</Box>
                    )}
                  </Box>
                )}
            </>
          )}
        </Box>

        {showClaimWall && (
          <ClaimWall
            {...{
              teamPlayer: localPlayer,
              claimReason: `Claim ${localPlayer?.nameFirst} to see their content`,
              onShowClaim: () => {
                setShowClaimWall(false);
                setShowClaim(true);
              },
              onClose: () => {
                setShowClaimWall(false);
              },
            }}
          />
        )}
        {showClaim &&
          playerPrimaryInfo?.teams &&
          playerPrimaryInfo.teams.length > 0 && (
            <ClaimPlayerLayer
              {...{
                team: playerPrimaryInfo.teams[0],
                teamPlayerId: playerId,
                closeLayer: () => setShowClaim(false),
              }}
            />
          )}
      </Box>
    );
  }
);

export default withLayout(withRouter(PlayerPage));
